import { useState } from 'react';
import { Heart, Send, Mail, Clock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

export const LoveNotesPage = () => {
  const [formData, setFormData] = useState({
    from: '',
    message: '',
    openDate: '5th Anniversary'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    // Visual mockup - no actual submission
    setTimeout(() => {
      setFormData({ from: '', message: '', openDate: '5th Anniversary' });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen pt-20 bg-[#0F0505]" data-testid="love-notes-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-[#722F37] rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-[#1B3A5F] rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          {/* Decorative Element */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-[#D4AF37]" />
            <Heart className="w-6 h-6 text-[#D4AF37]" fill="#D4AF37" />
            <div className="w-16 h-px bg-[#D4AF37]" />
          </div>

          <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Messages Across Time</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] mb-8">
            Love Notes
          </h1>
          <p className="text-[#A18E90] text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            Write a message to your future selves. Seal it with love, open it years from now.
          </p>
        </div>
      </section>

      {/* Send Future Message Section */}
      <section className="py-16 md:py-24 bg-[#1A0F0F]/50" data-testid="send-note-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="glass rounded-2xl p-8 md:p-12">
            {/* Section Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-6">
                <Send className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#F5F5F0] mb-4">
                Send Future Us a Message
              </h2>
              <p className="text-[#A18E90]">
                Write a note to open on our 5-year anniversary
              </p>
            </div>

            {/* Form */}
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="love-note-form">
                <div>
                  <label className="block text-[#D4AF37] text-sm tracking-wider uppercase mb-2">
                    From
                  </label>
                  <Input
                    type="text"
                    value={formData.from}
                    onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                    placeholder="Your name..."
                    className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-[#F5F5F0] placeholder:text-[#A18E90]/50"
                    data-testid="note-from-input"
                  />
                </div>

                <div>
                  <label className="block text-[#D4AF37] text-sm tracking-wider uppercase mb-2">
                    Your Message
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Dear Future Us..."
                    rows={6}
                    className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-[#F5F5F0] placeholder:text-[#A18E90]/50 resize-none"
                    data-testid="note-message-input"
                  />
                </div>

                <div>
                  <label className="block text-[#D4AF37] text-sm tracking-wider uppercase mb-2">
                    Open On
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-[#A18E90]">
                      <Clock className="w-5 h-5 text-[#D4AF37]" />
                      <span>5th Anniversary (February 2030)</span>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#F5F5F0] text-[#0F0505] font-semibold py-6 rounded-full transition-all duration-300"
                  data-testid="send-note-btn"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Seal This Note
                </Button>
              </form>
            ) : (
              <div className="text-center py-12 animate-fade-in" data-testid="note-success">
                <Sparkles className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
                <h3 className="font-['Playfair_Display'] text-2xl text-[#F5F5F0] mb-4">
                  Note Sealed with Love
                </h3>
                <p className="text-[#A18E90]">
                  Your message has been saved for Future Us to read.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 md:py-32 bg-[#0F0505]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <Heart className="w-8 h-8 text-[#D4AF37] mx-auto mb-8" fill="#D4AF37" />
          <blockquote className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#A18E90] italic leading-relaxed mb-8">
            "The greatest thing you'll ever learn is just to love and be loved in return."
          </blockquote>
          <div className="w-16 h-px bg-[#D4AF37] mx-auto mb-6" />
          <p className="text-[#D4AF37] text-sm tracking-widest uppercase">
            - Moulin Rouge
          </p>
        </div>
      </section>
    </div>
  );
};
